<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, onMounted, ref } from 'vue'
import { useFavoritesStore } from '@stores/favorites'
import DataLayer from '@services/data-layer/index'
import GenreGridBlock from '@components/genre/genre-grid-block'
import LibraryHeader from '@components/library/library-header'
import LibrarySearchResults from '@components/library/library-search-results'
import LibrarySectionTabs from '@components/library/library-section-tabs'

const favoritesStore = useFavoritesStore()

const isLoading = ref(false)
const allGenres = ref([])
const searchQuery = ref('')

const followedGenreIds = computed(() => favoritesStore.genreIds)
const genres = computed(() => allGenres.value.filter(genre => followedGenreIds.value.includes(genre.id)))

onMounted(async () => {
    isLoading.value = true
    const [genresRes] = await Promise.all([
        DataLayer.genres.getAll(),
        favoritesStore.fetchType('genres'),
    ])
    allGenres.value = genresRes.body || []
    isLoading.value = false
})

function handleToggleFollow(genre) {
    return favoritesStore.remove({ type: 'genres', id: genre.id })
}

usePageHead({ title: 'Your Genres', description: 'Your Genres' })
</script>

<template>
    <BaseContent>
        <BaseSection padded>
            <LibraryHeader
                title="Your Genres"
                :count="isLoading ? null : followedGenreIds.length"
                count-label="genres"
                :search-query="searchQuery"
                @update:search-query="searchQuery = $event"
            />
            <LibrarySearchResults
                v-if="searchQuery.trim()"
                :query="searchQuery"
            />
            <template v-else>
                <LibrarySectionTabs />
                <BaseSpinner v-if="isLoading" />
                <GenreGridBlock
                    v-else-if="genres.length > 0"
                    :genres="genres"
                    :followed-genre-ids="followedGenreIds"
                    @toggle-follow="handleToggleFollow"
                />
                <BaseEmptyList
                    v-else
                    :error="{
                        title: `You are not following any genres.`,
                        paragraph: 'Following genres helps us recommend similar genres.',
                    }"
                />
            </template>
        </BaseSection>
    </BaseContent>
</template>
